import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js'

const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID || ''
const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID || ''

const userPool = new CognitoUserPool({
  UserPoolId: userPoolId,
  ClientId: clientId,
})

export const useAuthStore = defineStore('auth', () => {
  const cognitoUser = ref<CognitoUser | null>(null)
  const session = ref<CognitoUserSession | null>(null)
  const initialized = ref(false)
  const loading = ref(true)
  const error = ref<string | null>(null)

  const isAuthenticated = computed(() => !!session.value?.isValid())

  const idToken = computed(() => session.value?.getIdToken())

  const user = computed(() => {
    if (!idToken.value) return null
    const payload = idToken.value.decodePayload()
    return {
      id: payload.sub,
      email: payload.email,
      username: payload['cognito:username'],
    }
  })

  const role = computed(() => {
    if (!idToken.value) return 'developer'
    const payload = idToken.value.decodePayload()
    // Check custom attribute or groups
    return payload['custom:role'] ||
           (payload['cognito:groups']?.includes('admin') ? 'admin' : 'developer')
  })

  const tenant = computed(() => {
    if (!idToken.value) return undefined
    const payload = idToken.value.decodePayload()
    return payload['custom:tenant'] as string | undefined
  })

  const isAdmin = computed(() => role.value === 'admin')
  const isDeveloper = computed(() => role.value === 'developer')

  async function initialize() {
    if (initialized.value) return

    loading.value = true
    try {
      const currentUser = userPool.getCurrentUser()
      if (currentUser) {
        cognitoUser.value = currentUser
        await refreshSession()
      }
      initialized.value = true
    } catch (e) {
      console.error('Failed to initialize auth:', e)
    } finally {
      loading.value = false
    }
  }

  function refreshSession(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!cognitoUser.value) {
        resolve()
        return
      }

      cognitoUser.value.getSession((err: Error | null, sess: CognitoUserSession | null) => {
        if (err) {
          session.value = null
          reject(err)
          return
        }
        session.value = sess
        resolve()
      })
    })
  }

  async function signIn(username: string, password: string): Promise<void> {
    error.value = null
    loading.value = true

    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: password,
    })

    const user = new CognitoUser({
      Username: username,
      Pool: userPool,
    })

    return new Promise((resolve, reject) => {
      user.authenticateUser(authDetails, {
        onSuccess: (sess) => {
          cognitoUser.value = user
          session.value = sess
          loading.value = false
          resolve()
        },
        onFailure: (err) => {
          error.value = err.message
          loading.value = false
          reject(err)
        },
        newPasswordRequired: () => {
          error.value = 'Password change required'
          loading.value = false
          reject(new Error('Password change required'))
        },
      })
    })
  }

  function signOut() {
    if (cognitoUser.value) {
      cognitoUser.value.signOut()
    }
    cognitoUser.value = null
    session.value = null
  }

  async function signUp(email: string, password: string): Promise<void> {
    error.value = null
    loading.value = true

    const attributeList = [
      new CognitoUserAttribute({
        Name: 'email',
        Value: email,
      }),
    ]

    return new Promise((resolve, reject) => {
      userPool.signUp(email, password, attributeList, [], (err, result) => {
        loading.value = false
        if (err) {
          error.value = err.message
          reject(err)
          return
        }
        if (result?.user) {
          cognitoUser.value = result.user
        }
        resolve()
      })
    })
  }

  async function confirmSignUp(username: string, code: string): Promise<void> {
    error.value = null
    loading.value = true

    const user = new CognitoUser({
      Username: username,
      Pool: userPool,
    })

    return new Promise((resolve, reject) => {
      user.confirmRegistration(code, true, (err) => {
        loading.value = false
        if (err) {
          error.value = err.message
          reject(err)
          return
        }
        resolve()
      })
    })
  }

  async function resendConfirmationCode(username: string): Promise<void> {
    error.value = null
    loading.value = true

    const user = new CognitoUser({
      Username: username,
      Pool: userPool,
    })

    return new Promise((resolve, reject) => {
      user.resendConfirmationCode((err) => {
        loading.value = false
        if (err) {
          error.value = err.message
          reject(err)
          return
        }
        resolve()
      })
    })
  }

  async function getToken(): Promise<string | null> {
    if (!session.value?.isValid()) {
      try {
        await refreshSession()
      } catch {
        return null
      }
    }
    return session.value?.getIdToken().getJwtToken() || null
  }

  async function forgotPassword(username: string): Promise<void> {
    error.value = null
    loading.value = true

    const user = new CognitoUser({
      Username: username,
      Pool: userPool,
    })

    return new Promise((resolve, reject) => {
      user.forgotPassword({
        onSuccess: () => {
          loading.value = false
          resolve()
        },
        onFailure: (err) => {
          error.value = err.message
          loading.value = false
          reject(err)
        },
      })
    })
  }

  async function confirmPassword(
    username: string,
    code: string,
    newPassword: string
  ): Promise<void> {
    error.value = null
    loading.value = true

    const user = new CognitoUser({
      Username: username,
      Pool: userPool,
    })

    return new Promise((resolve, reject) => {
      user.confirmPassword(code, newPassword, {
        onSuccess: () => {
          loading.value = false
          resolve()
        },
        onFailure: (err) => {
          error.value = err.message
          loading.value = false
          reject(err)
        },
      })
    })
  }

  return {
    cognitoUser,
    session,
    user,
    initialized,
    loading,
    error,
    isAuthenticated,
    role,
    tenant,
    isAdmin,
    isDeveloper,
    initialize,
    signIn,
    signUp,
    confirmSignUp,
    resendConfirmationCode,
    signOut,
    getToken,
    refreshSession,
    forgotPassword,
    confirmPassword,
  }
})
